import { useState, useEffect } from 'react';
import { apiService } from '../../services/ApiService';

interface UseLocationHierarchyParams {
  selectedAssetId: string | undefined;
  isAuthenticated: boolean;
}

/**
 * Location hierarchy for breadcrumb display (buildings → storeys → spaces + a
 * per-location path map), extracted verbatim from App.tsx (F-2; only
 * `selectedAsset?.id` reads became the injected `selectedAssetId`). Fetches
 * /locations/tree on asset change; the setter is internal — consumers only read.
 */
export const useLocationHierarchy = ({ selectedAssetId, isAuthenticated }: UseLocationHierarchyParams) => {
  const [locationHierarchy, setLocationHierarchy] = useState<{
    buildings: Array<{ id: string; name: string; storeys: Array<{ id: string; name: string; spaces: Array<{ id: string; name: string }> }> }>;
    pathMap: Record<string, { building?: string; storey?: string; space?: string }>;
  } | null>(null);

  // Fetch location hierarchy for breadcrumb display
  useEffect(() => {
    const fetchLocationHierarchy = async () => {
      if (!selectedAssetId || !isAuthenticated) {
        setLocationHierarchy(null);
        return;
      }

      try {
        // Use apiService to include auth header
        const locations = await apiService.get(`/assets/${selectedAssetId}/locations/tree`);

        // Process locations to extract buildings, storeys, and spaces + path map
        const buildings: Array<{ id: string; name: string; storeys: Array<{ id: string; name: string; spaces: Array<{ id: string; name: string }> }> }> = [];
        const pathMap: Record<string, { building?: string; storey?: string; space?: string }> = {};

        const processLocation = (loc: any, parentBuilding?: any, parentStorey?: any) => {
          const locType = (loc.type || '').toLowerCase();

          if (locType === 'building' || locType === 'ifcbuilding') {
            const building = { id: loc.id, name: loc.name, storeys: [] as Array<{ id: string; name: string; spaces: Array<{ id: string; name: string }> }> };
            buildings.push(building);
            pathMap[loc.id] = { building: loc.name };
            if (loc.children) {
              loc.children.forEach((child: any) => processLocation(child, building));
            }
          } else if (locType === 'buildingstorey' || locType === 'ifcbuildingstorey' || locType === 'building storey') {
            const storey = { id: loc.id, name: loc.name, spaces: [] as Array<{ id: string; name: string }> };
            if (parentBuilding) {
              parentBuilding.storeys.push(storey);
              pathMap[loc.id] = { building: parentBuilding.name, storey: loc.name };
            } else {
              // Storey without a building parent - still track in pathMap
              pathMap[loc.id] = { storey: loc.name };
            }
            if (loc.children) {
              loc.children.forEach((child: any) => processLocation(child, parentBuilding, storey));
            }
          } else if (locType === 'spaces' || locType === 'ifcspace' || locType === 'space') {
            if (parentStorey) {
              parentStorey.spaces.push({ id: loc.id, name: loc.name });
            }
            pathMap[loc.id] = { building: parentBuilding?.name, storey: parentStorey?.name, space: loc.name };
          } else if (loc.children) {
            // Continue traversing for other types (like Site, Project)
            loc.children.forEach((child: any) => processLocation(child, parentBuilding, parentStorey));
          }
        };

        locations.forEach((loc: any) => processLocation(loc));
        setLocationHierarchy({ buildings, pathMap });
      } catch (error) {
        console.error('Failed to fetch location hierarchy:', error);
        setLocationHierarchy(null);
      }
    };

    fetchLocationHierarchy();
  }, [selectedAssetId, isAuthenticated]);

  return { locationHierarchy };
};
